import { WorkspaceFile } from '@/models/file';
import { LibraryService } from '@/services/library.service';
import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const useRecentlyUploadedFiles = (
  orgID: string,
  setFiles: Dispatch<SetStateAction<WorkspaceFile[]>>
): {
  recentlyUploadedFiles: WorkspaceFile[];
  setRecentlyUploadedFiles: Dispatch<SetStateAction<WorkspaceFile[]>>;
  foundFiles: WorkspaceFile[];
  isSuccess: boolean;
} => {
  const [recentlyUploadedFiles, setRecentlyUploadedFiles] = useState<
    WorkspaceFile[]
  >([]);

  const { data: foundFiles = [], isSuccess } = useQuery<WorkspaceFile[]>({
    queryKey: ['recentlyUploadedFiles', recentlyUploadedFiles, orgID],
    queryFn: async () => {
      if (recentlyUploadedFiles.length === 0) return [];
      const foundFiles: WorkspaceFile[] = [];
      const timeoutMs = 300000;
      const currentMs = new Date().getTime();

      const removeFile = (file: WorkspaceFile) => {
        setRecentlyUploadedFiles((prev) =>
          prev.filter((f) => f.id !== file.id)
        );

        toast.error(
          `An error happened while processing "${file.name}", please try again later.`,
          {
            autoClose: 5000,
          }
        );
      };

      for (const file of recentlyUploadedFiles) {
        console.log('Looking for file:', file.name);
        if (file.date.getTime() + timeoutMs < currentMs) {
          removeFile(file);
          continue;
        }

        const { ready, uploadStatus, ingestStatus } =
          await LibraryService.getFileStatus(file.id, orgID);

        if (ready) {
          const fetchedFile = await LibraryService.getFile(file.id, orgID);
          if (fetchedFile) {
            foundFiles.push(fetchedFile);

            toast.success(
              `"${fetchedFile.name}" uploaded and processed successfully.`,
              {
                autoClose: 5000,
              }
            );
          } else {
            removeFile(file);
          }
        } else if (uploadStatus === 'FAILED' || ingestStatus === 'FAILED') {
          removeFile(file);
        }
      }
      return foundFiles;
    },
    enabled: recentlyUploadedFiles.length > 0,
    refetchInterval: 3500,
  });

  useUpdateFiles(isSuccess, foundFiles, setFiles, setRecentlyUploadedFiles);

  return {
    recentlyUploadedFiles,
    setRecentlyUploadedFiles,
    foundFiles,
    isSuccess,
  };
};

const useUpdateFiles = (
  isSuccess: boolean,
  foundFiles: WorkspaceFile[],
  setFiles: Dispatch<SetStateAction<WorkspaceFile[]>>,
  setRecentlyUploadedFiles: Dispatch<SetStateAction<WorkspaceFile[]>>
) => {
  useEffect(() => {
    if (isSuccess && foundFiles.length > 0) {
      setRecentlyUploadedFiles((prev) =>
        prev.filter((f) => !foundFiles.some((ff) => ff.id === f.id))
      );
      setFiles((prevFiles) => {
        const merged = [...prevFiles, ...foundFiles];
        merged.sort((a, b) => b.date.getTime() - a.date.getTime());
        return merged;
      });
    }
  }, [isSuccess, foundFiles, setRecentlyUploadedFiles, setFiles]);
};

export default useRecentlyUploadedFiles;
